// pages/SettingsPage.jsx
import React, { useState, useEffect } from "react";

import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";


import { Box, Stack, Typography, CircularProgress, Container } from "@mui/material";
import CustomCard from "../../components/common/CustomCard";
import CustomButton from "../../components/common/CustomButton";
import CustomInput from "../../components/common/CustomInput";
import CustomHeader from "../../components/common/CustomHeader";
import { configurationApi } from "../../api/configurationApi";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { DotsLoader } from "../../components/common/spinLoader";
import * as Yup from "yup";
import SkeletonLoader from "../../components/common/SkeletonLoader";

// ✅ Yup Number Validation Schema
const numberSchema = Yup.number()
    .typeError("Only numbers allowed")
    .positive("Must be greater than 0")
    .integer("Only whole numbers allowed")
    .required("This field is required");

const SettingsPage = () => {
    const [questionPerSkillOnJD, setQuestionPerSkillOnJD] = useState("");
    const [questionPerInterview, setQuestionPerInterview] = useState("");
    const [apiConfig, setApiConfig] = useState("");
    const [loading, setLoading] = useState(true); // ✅ Loader state
    const [saving, setSaving] = useState(false); // ✅ saving loader state
    const [errors, setErrors] = useState({
        skill: "",
        interview: ""
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "",
    });

    // ✅ Fetch settings from backend
    const fetchSettings = async () => {
        try {


            setLoading(true);

            const response = await configurationApi.getConfiguration();
            // console.log("Configuration response:", response);

            const { status, message, data } = response || {};

            /* 1️⃣ Network error */
            if (status === 0) {
                setSnackbar({
                    open: true,
                    message:
                        "🌐 Network error. Please check your internet connection.",
                    severity: "warning",
                });
                return;
            }

            /* 2️⃣ Not found */
            if (status === 404) {
                setSnackbar({
                    open: true,
                    message: "Configuration not found.",
                    severity: "warning",
                });
                return;
            }

            /* 3️⃣ Server error */
            if (status >= 500) {
                setSnackbar({
                    open: true,
                    message:
                        message || "❌ Server error while fetching configuration.",
                    severity: "error",
                });
                return;
            }

            /* 4️⃣ Client error */
            if (status >= 400) {
                setSnackbar({
                    open: true,
                    message:
                        message || "⚠️ Failed to fetch configuration.",
                    severity: "error",
                });
                return;
            }

            /* 5️⃣ Success */
            if (status === 200 && data) {
                setQuestionPerSkillOnJD(Number(data.no_of_question_per_skill ?? 0));
                setQuestionPerInterview(Number(data.no_of_question_per_interview ?? 0));
                return;
            }

            /* 6️⃣ Fallback */
            setSnackbar({
                open: true,
                message: "⚠️ Unexpected response while loading configuration.",
                severity: "warning",
            });

        } catch (error) {
            console.error("Error fetching settings:", error);
            setSnackbar({
                open: true,
                message:
                    "❌ Something went wrong while fetching settings.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    // ✅ Validate & Save config
    const handleSave = async () => {
        let valid = true;
        setErrors({ skill: "", interview: "" });

        try {
            await numberSchema.validate(questionPerSkillOnJD);
        } catch (e) {
            valid = false;
            setErrors(prev => ({ ...prev, skill: e.message }));
        }

        try {
            await numberSchema.validate(questionPerInterview);
        } catch (e) {
            valid = false;
            setErrors(prev => ({ ...prev, interview: e.message }));
        }

        if (!valid) return; // ❌ Stop if any validation failed

        try {
            setSaving(true);
            const payload = {
                no_of_question_per_interview: Number(questionPerInterview),
                no_of_question_per_skill: Number(questionPerSkillOnJD),
                iscloudconfiguration: apiConfig?.isCloudConfiguration ?? false,
                apikeys: apiConfig?.apiKeys || null,
            };

            const response = await configurationApi.saveConfiguration(payload);
            // console.log("Save configuration response:", response);

            const { status, message, data } = response || {};
            /* 1️⃣ Network error */
            if (status === 0) {
                setSnackbar({
                    open: true,
                    message:
                        "🌐 Network error. Please check your internet connection.",
                    severity: "warning",
                });
                return;
            }

            /* 2️⃣ Server error */
            if (status >= 500) {
                setSnackbar({
                    open: true,
                    message:
                        message || "❌ Server error while saving configuration.",
                    severity: "error",
                });
                return;
            }

            /* 3️⃣ Client / validation error */
            if (status >= 400) {
                setSnackbar({
                    open: true,
                    message:
                        message || "⚠️ Failed to save configuration.",
                    severity: "error",
                });
                return;
            }

            /* 4️⃣ Success */
            if (status === 200 || status === 201) {
                setSnackbar({
                    open: true,
                    message:
                        message || "Configuration saved successfully!",
                    severity: "success",
                });
                return;
            }

            /* 5️⃣ Fallback */
            setSnackbar({
                open: true,
                message:
                    "⚠️ Unexpected response while saving configuration.",
                severity: "warning",
            });

        } catch (err) {
            console.error("Save configuration error:", err);
            setSnackbar({
                open: true,
                message:
                    "❌ Something went wrong while saving settings.",
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };



    // ✅ Loader UI
    return (
        <Box>

            {/* ✅ Always Visible Header */}
            <CustomHeader title="Settings Page" />

            {/* ✅ Content Area */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                {loading ? (
                    /* ✅ Show SkeletonLoader only for the settings card */
                    <Container maxWidth="xl">
                        <SkeletonLoader type="settings" rows={6} />
                    </Container>
                ) : (
                    /* ✅ Actual Settings Form */
                    <CustomCard
                        sx={{ maxWidth: 600, p: 4, borderRadius: 3, boxShadow: 3 }}
                        size={{ height: "100%", width: "100%" }}
                    >
                        <Stack spacing={3}>

                            {/* ✅ JD Questions */}
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography fontWeight="bold" fontSize={14}>
                                        Count of Question To be Generate on each Skill
                                    </Typography>

                                    <Tooltip
                                        title="Number of questions will generate per skill listed in Job Description"
                                        arrow
                                        placement="top"
                                    >
                                        <IconButton size="small">
                                            <InfoOutlinedIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>

                                <CustomInput
                                    disabled={saving}
                                    type="text"
                                    value={questionPerSkillOnJD}
                                    onChange={(e) => setQuestionPerSkillOnJD(e.target.value)}
                                    placeholder="Enter number of questions"
                                />

                                {errors.skill && (
                                    <span style={{ color: "red", fontSize: 12 }}>
                                        {errors.skill}
                                    </span>
                                )}
                            </Stack>


                            {/* ✅ Interview Questions */}
                            <Stack spacing={1}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography fontWeight="bold" fontSize={14}>
                                        Number of Question To be ask in Interview.
                                    </Typography>

                                    <Tooltip
                                        title="Total number of questions asked during the interview. This impacts interview duration and scoring depth."
                                        arrow
                                        placement="top"
                                    >
                                        <IconButton size="small">
                                            <InfoOutlinedIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>

                                <CustomInput
                                    disabled={saving}
                                    type="text"
                                    value={questionPerInterview}
                                    onChange={(e) => setQuestionPerInterview(e.target.value)}
                                    placeholder="Enter number of questions"
                                />

                                {errors.interview && (
                                    <span style={{ color: "red", fontSize: 12 }}>
                                        {errors.interview}
                                    </span>
                                )}
                            </Stack>


                            {/* ✅ Save Button */}
                            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                <CustomButton
                                    onClick={handleSave}
                                    disabled={saving}
                                    sx={{
                                        px: 5,
                                        py: 1.5,
                                        fontWeight: "bold",
                                        position: "relative",
                                    }}
                                >
                                    {/* Button text (always rendered) */}
                                    <span style={{ opacity: saving ? 0 : 1 }}>
                                        Save Configuration
                                    </span>

                                    {/* Loader overlay */}
                                    {saving && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <DotsLoader size={30} color="green" />
                                        </Box>
                                    )}
                                </CustomButton>
                            </Box>
                        </Stack>
                    </CustomCard>
                )}
            </Box>

            {/* ✅ Snackbar */}
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                autoHideDuration={6000}
            />
        </Box>
    );

};

export default SettingsPage;
